// src/pages/LatestBrowserUpdates.jsx
import React, { useEffect, useState, useCallback, useContext } from "react";
import { format } from "date-fns";
import { request } from "../utils/request";
import { ColorModeContext } from "../index";
import CommonDeleteModal from '../components/Common/CommonDeleteModal';

import {
  IconButton,
  Box,
  Breadcrumbs,
  Link,
  Checkbox,
  Menu,
  MenuItem,
  Typography,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Paper,
  Chip,
  CircularProgress,
  TablePagination,
  InputAdornment,
  Select
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import BlockIcon from '@mui/icons-material/Block';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import RefreshIcon from '@mui/icons-material/Refresh';
import LaunchIcon from '@mui/icons-material/Launch';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LayersIcon from '@mui/icons-material/Layers';
import LinkIcon from '@mui/icons-material/Link';
import ArticleIcon from '@mui/icons-material/Article';
import SellIcon from '@mui/icons-material/Sell';
import CloseIcon from '@mui/icons-material/Close';

export default function LatestBrowserUpdates() {
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === 'dark';

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = currentUser.superadmin === true;
  const userPermissions = currentUser.permissions || [];
  const hasPermission = (perm) => isSuperAdmin || userPermissions.includes(perm);

  const canCreate = hasPermission('Browser Control-Browser Updates-Browser UpdatesWrite');
  const canEdit = hasPermission('Browser Control-Browser Updates-Browser UpdatesUpdate');
  const canDelete = hasPermission('Browser Control-Browser Updates-Browser UpdatesDelete');
  
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 25 });
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ version: "", link: "", notes: "", isLatest: false });

  const [selectedIds, setSelectedIds] = useState([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);

  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);

  const fetchPage = useCallback(async (page = 1, limit = 25) => {
    setLoading(true);
    try {
      const resp = await request(`browser-updates?page=${page}&limit=${limit}`, { method: "GET" });
      if (resp && resp.success && Array.isArray(resp.data)) {
        setItems(resp.data);
        setMeta(resp.meta || { total: resp.data.length, page, limit });
      } else if (Array.isArray(resp)) {
        setItems(resp);
        setMeta({ total: resp.length, page, limit });
      } else {
        const arr = resp && resp.data ? resp.data : [];
        setItems(Array.isArray(arr) ? arr : []);
        setMeta({ total: Array.isArray(arr) ? arr.length : 0, page, limit });
      }
    } catch (err) {
      console.error("Failed to fetch browser-updates:", err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChangePage = (event, nextPage) => {
    fetchPage(nextPage + 1, meta.limit);
  };

  const handleChangeRowsPerPage = (event) => {
    fetchPage(1, Number.parseInt(event.target.value, 10));
  };

  useEffect(() => {
    fetchPage(1, 25);
  }, [fetchPage]);

  const openCreate = () => {
    if (!canCreate) return;
    setEditItem(null);
    setForm({ version: "", link: "", notes: "", isLatest: false });
    setModalOpen(true);
  };

  const openEdit = (it) => {
    if (!canEdit) return;
    setEditItem(it);
    setForm({
      version: it.version || "",
      link: it.link || "",
      notes: it.notes || "",
      isLatest: !!it.isLatest
    });
    setModalOpen(true);
  };

  const submit = async () => {
    if (!editItem && !canCreate) return;
    if (editItem && !canEdit) return;
    if (!form.version || !form.link) {
      alert("Version and link are required");
      return;
    }
    setLoading(true);
    try {
      if (editItem && editItem._id) {
        await request(`browser-updates/${editItem._id}`, { method: "PUT", body: form });
      } else {
        await request("browser-updates", { method: "POST", body: form });
      }
      setModalOpen(false);
      await fetchPage(1, meta.limit);
    } catch (err) {
      console.error("Failed to save:", err);
      alert("Save failed");
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setDeleteOpen(false);
    setDeletingItem(null);
  };

  const confirmDelete = async () => {
    if (!deletingItem) return;
    setDeleting(true);
    try {
      await request(`browser-updates/${deletingItem._id}`, { method: "DELETE" });
      setSelectedIds((prev) => prev.filter((id) => id !== deletingItem._id));
      await fetchPage(1, meta.limit);
      cancelDelete();
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const handleDelete = (it) => {
    if (!canDelete) return;
    setDeletingItem(it);
    setDeleteOpen(true);
  };

  const toggleLatest = async (it) => {
    if (!canEdit) return;
    setLoading(true);
    try {
      await request(`browser-updates/${it._id}`, { method: "PUT", body: { isLatest: true } });
      await fetchPage(1, meta.limit);
    } catch (err) {
      console.error("Failed to set latest:", err);
      alert("Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      return format(new Date(iso), "yyyy-MM-dd HH:mm:ss");
    } catch {
      return String(iso);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const isAllSelected = items.length > 0 && selectedIds.length === items.length;

  const toggleSelectAll = () => {
    if (isAllSelected) setSelectedIds([]);
    else setSelectedIds(items.map((it) => it._id));
  };

  const clearSelection = () => setSelectedIds([]);

  const handleEditSelected = () => {
    if (!canEdit) return;
    if (selectedIds.length === 0) return;
    if (selectedIds.length > 1) {
      alert("Please select only one item to edit.");
      return;
    }
    const it = items.find(i => i._id === selectedIds[0]);
    if (it) {
      openEdit(it);
    } else {
      alert("Selected item not found.");
    }
  };

  const handleDeleteSelected = () => {
    if (!canDelete) return;
    if (selectedIds.length === 0) return;
    setBulkDeleteOpen(true);
  };

  const handleCancelBulkDelete = () => {
    setBulkDeleteOpen(false);
    setBulkDeleting(false);
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setBulkDeleting(true);
    try {
      await Promise.all(selectedIds.map(id => request(`browser-updates/${id}`, { method: "DELETE" })));
      await fetchPage(1, meta.limit);
      setSelectedIds([]);
      handleCancelBulkDelete();
    } catch (err) {
      console.error("Bulk delete failed:", err);
      alert("Bulk delete failed");
      setBulkDeleting(false);
    }
  };

  const handleOpenRowMenu = (e, it) => {
    setMenuAnchorEl(e.currentTarget);
    setMenuRowId(it._id);
  };
  
  const handleCloseRowMenu = () => {
    setMenuAnchorEl(null);
    setMenuRowId(null);
  };
  
  const handleRowEdit = () => {
    if (!canEdit) return;
    const it = items.find(i => i._id === menuRowId);
    if (it) openEdit(it);
    handleCloseRowMenu();
  };
  
  const handleRowDelete = () => {
    if (!canDelete) return;
    const it = items.find(i => i._id === menuRowId);
    if (it) handleDelete(it);
    handleCloseRowMenu();
  };

  const inputStyle = {
    width: '100%',
    '& .MuiInputBase-root': { 
      backgroundColor: isDark ? '#111827' : '#fff',
      borderRadius: '6px',
    },
    '& .MuiInputBase-input': { 
      color: isDark ? '#F9FAFB' : 'inherit' 
    },
    '& input::placeholder': {
      color: '#9CA3AF',
      opacity: 1,
    },
    '& .MuiInputLabel-root': { 
      color: isDark ? '#9CA3AF' : 'inherit',
      backgroundColor: isDark ? '#111827' : '#fff',
      padding: '0 4px',
      marginLeft: '-4px',
    },
    '& .MuiOutlinedInput-notchedOutline': { 
      borderColor: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0,0,0,0.23)' 
    },
    '&:hover .MuiOutlinedInput-notchedOutline': { 
      borderColor: isDark ? 'rgba(255, 255, 255, 0.3) !important' : 'rgba(0,0,0,0.87) !important' 
    },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#3B82F6 !important',
      borderWidth: '2px',
    },
    '& .MuiSvgIcon-root': { color: '#9CA3AF' }
  };

  return (
    <Box p={3} sx={{ backgroundColor: isDark ? 'transparent' : '#FAFAFA', minHeight: '100vh', boxSizing: 'border-box' }}>
      <Box mb={3}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.875rem', mb: 1 }}>
          <Link
            href="/home"
            underline="hover"
            style={{
              textDecoration: "none",
              color: isDark ? "#90caf9" : "#0066FF",
              transition: "color 0.2s ease",
            }}
          >
            Dashboard
          </Link>
          <Typography sx={{ fontSize: "0.875rem", color: isDark ? "#fff" : "#111827", margin: 0 }}>
            Browser Updates
          </Typography>
        </Breadcrumbs>

        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="body2" sx={{ color: isDark ? "#9CA3AF" : "#6B7280" }}>
              Manage browser update versions and release links for your organization.
            </Typography>
          </Box>
          {/* {canCreate && (
            <Button
              variant="contained"
              onClick={openCreate}
              startIcon={<AddIcon />}
              sx={{
                backgroundColor: '#0066FF',
                color: '#fff',
                textTransform: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                px: 2.5,
                py: 1,
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#0052cc', boxShadow: 'none' }
              }}
            >
              Add Update
            </Button>
          )} */}
        </Stack>
      </Box>



      {selectedIds.length > 0 && (
        <Box mb={2} display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" sx={{ color: isDark ? '#fff' : 'inherit' }}>{selectedIds.length} selected</Typography>

          {canEdit && (
            <Button
              size="small"
              variant="outlined"
              onClick={handleEditSelected}
              disabled={selectedIds.length !== 1}
              sx={{ borderColor: isDark ? '#fff' : undefined, color: isDark ? '#fff' : undefined }}
            >
              Edit
            </Button>
          )}

          {canDelete && (
            <Button
              size="small"
              variant="contained"
              onClick={handleDeleteSelected}
              sx={{ backgroundColor: '#dc3545', color: '#fff', '&:hover': { backgroundColor: '#b71c1c' } }}
            >
              Delete
            </Button>
          )}

          <Button
            size="small"
            onClick={clearSelection}
            sx={{ color: isDark ? '#fff' : undefined }}
          >
            Clear
          </Button>
        </Box>
      )}

      <Paper 
        elevation={0}
        sx={{ 
          mb: 2, 
          height: 'calc(100vh - 240px)',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: isDark ? '#111827' : '#fff',
          borderRadius: '12px',
          border: isDark ? '1px solid #374151' : '1px solid #F3F4F6',
          boxShadow: isDark ? 'none' : '0px 1px 2px rgba(0, 0, 0, 0.05)',
          overflow: 'hidden'
        }}
      >
        <Box 
          sx={{ 
            flexGrow: 1, 
            overflow: 'auto', 
            width: '100%',
            scrollbarWidth: 'thin',
            scrollbarColor: isDark ? 'rgba(255, 255, 255, 0.2) transparent' : 'rgba(0, 0, 0, 0.2) transparent',
            '&::-webkit-scrollbar': { width: '6px', height: '6px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)', borderRadius: '10px' },
            '&::-webkit-scrollbar-thumb:hover': { background: isDark ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.35)' }
          }}
        >
          <Box component="table" width="100%" sx={{ borderCollapse: 'collapse', minWidth: 1000 }}>
            <Box component="thead">
              <Box component="tr">
                <Box
                  component="th"
                  p={2}
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: isDark ? '#1F2937' : '#fff',
                    borderBottom: isDark ? '1px solid #374151' : '1px solid #F3F4F6',
                    width: 48,
                    textAlign: 'center'
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={isAllSelected}
                    indeterminate={selectedIds.length > 0 && !isAllSelected}
                    onChange={toggleSelectAll}
                    inputProps={{ 'aria-label': 'select all updates' }}
                    sx={{ color: isDark ? '#9CA3AF' : '#D1D5DB', '&.Mui-checked': { color: '#0066FF' } }}
                  />
                </Box>
                {[
                  { label: 'Version', align: 'left', width: 120 },
                  { label: 'Link', align: 'left', width: 300 },
                  { label: 'Notes', align: 'left', width: 200 },
                  { label: 'Latest', align: 'left', width: 120 },
                  { label: 'Created At', align: 'left', width: 180 },
                  ...(canEdit || canDelete ? [{ label: 'Actions', align: 'center', width: 80 }] : [])
                ].map((col, i) => (
                  <Box
                    component="th"
                    key={i}
                    p={2}
                    textAlign={col.align}
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      width: col.width,
                      backgroundColor: isDark ? '#1F2937' : '#fff',
                      color: isDark ? '#fff' : '#111827',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      borderBottom: isDark ? '1px solid #374151' : '1px solid #F3F4F6'
                    }}
                  >
                    {col.label}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {loading && items.length === 0 ? (
                <Box component="tr">
                  <Box component="td" colSpan={7} textAlign="center" p={3}>
                    <CircularProgress size={24} sx={{ color: isDark ? '#fff' : 'primary.main' }}/>
                  </Box>
                </Box>
              ) : items.length === 0 ? (
                <Box component="tr">
                  <Box component="td" colSpan={7} textAlign="center" p={3} sx={{ color: isDark ? '#9CA3AF' : 'text.secondary' }}>
                    No updates found.
                  </Box>
                </Box>
              ) : (
                items.map((it) => (
                  <Box 
                    component="tr" 
                    key={it._id}
                    sx={{
                      borderBottom: isDark ? '1px solid #374151' : '1px solid #F3F4F6',
                      height: '64px',
                      '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : '#F9FAFB' }
                    }}
                  >
                    <Box component="td" p={2} textAlign="center" sx={{ verticalAlign: 'middle' }}>
                      <Checkbox
                        size="small"
                        checked={selectedIds.includes(it._id)}
                        onChange={() => toggleSelect(it._id)}
                        inputProps={{ "aria-label": `select-${it._id}` }}
                        sx={{ color: isDark ? '#9CA3AF' : '#D1D5DB', '&.Mui-checked': { color: '#0066FF' } }}
                      />
                    </Box>
                    <Box component="td" p={2} sx={{ color: isDark ? '#fff' : '#111827', fontWeight: 600, fontSize: '0.875rem', verticalAlign: 'middle' }}>{it.version}</Box>
                    <Box component="td" p={2} sx={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: 'middle', fontSize: '0.875rem' }}>
                      <a href={it.link} target="_blank" rel="noreferrer" style={{ color: isDark ? '#90caf9' : '#0066FF', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {it.link}
                        <LaunchIcon sx={{ fontSize: 14 }} />
                      </a>
                    </Box>
                    <Box component="td" p={2} sx={{ color: isDark ? '#9CA3AF' : '#6B7280', fontSize: '0.875rem', verticalAlign: 'middle' }}>{it.notes || "—"}</Box>
                    <Box component="td" p={2} sx={{ verticalAlign: 'middle' }}>
                      {it.isLatest ? (
                        <Box sx={{ 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          px: 1.5, 
                          py: 0.5, 
                          backgroundColor: isDark ? "rgba(16, 185, 129, 0.2)" : "#ECFDF5", 
                          color: isDark ? "#34d399" : "#10B981",
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          whiteSpace: 'nowrap'
                        }}>
                          Latest
                        </Box>
                      ) : canEdit ? (
                        <Box 
                          component="button"
                          onClick={() => toggleLatest(it)} 
                          sx={{ 
                            border: 'none',
                            cursor: 'pointer',
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            px: 1.5, 
                            py: 0.5, 
                            backgroundColor: isDark ? "rgba(59, 130, 246, 0.2)" : "#EFF6FF", 
                            color: isDark ? "#60a5fa" : "#0066FF",
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            '&:hover': { backgroundColor: isDark ? "rgba(59, 130, 246, 0.3)" : "#DBEAFE" }
                          }}
                        >
                          Make latest
                        </Box>
                      ) : <Typography sx={{ color: isDark ? '#9CA3AF' : 'text.secondary' }}>—</Typography>}
                    </Box>
                    <Box component="td" p={2} sx={{ color: isDark ? '#9CA3AF' : '#6B7280', fontSize: '0.875rem', verticalAlign: 'middle' }}>{fmt(it.createdAt)}</Box>
                    
                    {(canEdit || canDelete) && (
                      <Box component="td" p={2} textAlign="center" sx={{ verticalAlign: 'middle' }}>
                        <IconButton
                          size="small"
                          aria-controls={menuRowId === it._id ? `row-menu-${it._id}` : undefined}
                          aria-haspopup="true"
                          onClick={(e) => handleOpenRowMenu(e, it)}
                          sx={{ color: isDark ? '#9CA3AF' : '#6B7280' }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Box>
        <Box sx={{ borderTop: isDark ? '1px solid #374151' : '1px solid #F3F4F6', backgroundColor: isDark ? '#111827' : '#fff' }}>
          <TablePagination
            component="div"
            count={meta.total}
            page={Math.max(0, (meta.page || 1) - 1)}
            rowsPerPage={meta.limit || 25}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ 
              flexShrink: 0,
              color: isDark ? '#9CA3AF' : '#6B7280',
              '& .MuiTablePagination-selectIcon': { color: isDark ? '#9CA3AF' : '#6B7280' },
              '& .MuiTablePagination-actions': { color: isDark ? '#9CA3AF' : '#6B7280' }
            }}
          />
        </Box>
      </Paper>

      <Menu
        id={menuRowId ? `row-menu-${menuRowId}` : undefined}
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl) && Boolean(menuRowId)}
        onClose={handleCloseRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { backgroundColor: isDark ? '#111827' : '#fff', color: isDark ? '#F9FAFB' : 'inherit', backgroundImage: 'none' } }}
      >
        {canEdit && <MenuItem onClick={handleRowEdit}>Edit</MenuItem>}
        {canDelete && <MenuItem onClick={handleRowDelete} sx={{ color: '#d32f2f' }}>Delete</MenuItem>}
      </Menu>

      <Dialog 
        open={modalOpen} 
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        BackdropProps={{
          sx: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0, 0, 0, 0.4)'
          }
        }}
        sx={{
          '& .MuiDialog-paper': {
            width: '850px',
            maxWidth: '100%',
            borderRadius: '16px',
            overflow: 'hidden',
            margin: { xs: '16px', md: '24px' },
            maxHeight: { xs: 'calc(100% - 32px)', md: 'calc(100% - 48px)' },
            height: { xs: 'calc(100% - 32px)', md: 'auto' }
          },
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} sx={{ height: { xs: '100%', md: 'auto' }, maxHeight: { xs: '100%', md: 'calc(100vh - 64px)' }, flexGrow: 1 }}>
          {/* LEFT SIDE */}
          <Box sx={{ 
            width: { xs: '100%', md: '35%' }, 
            display: { xs: 'none', md: 'flex' },
            background: isDark ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)' : 'linear-gradient(135deg, #F0F7FF 0%, #E0F2FE 100%)', 
            p: 4,
            flexDirection: 'column',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <Box sx={{
              width: 48,
              height: 48,
              borderRadius: '12px',
              backgroundColor: isDark ? 'rgba(59, 130, 246, 0.2)' : '#DBEAFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3
            }}>
              <FileDownloadIcon sx={{ color: '#0066FF', fontSize: 28 }} />
            </Box>
            
            <Typography variant="h4" sx={{ fontWeight: 800, color: isDark ? '#fff' : '#111827', mb: 2, lineHeight: 1.2, fontSize: '1.75rem' }}>
              {editItem ? "Edit Browser Update" : "Add Browser Update"}
            </Typography>
            <Typography variant="body2" sx={{ color: isDark ? '#94A3B8' : '#475569', mb: 4, lineHeight: 1.6 }}>
              Add a new browser version and release link to keep your users updated with the latest release.
            </Typography>

            <Stack spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{ minWidth: 40, width: 40, height: 40, borderRadius: '50%', backgroundColor: isDark ? 'rgba(59,130,246,0.2)' : '#E0F2FE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <LayersIcon sx={{ color: '#0066FF', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? '#fff' : '#1E293B', mb: 0.5 }}>Manage Versions</Typography>
                  <Typography variant="body2" sx={{ color: isDark ? '#94A3B8' : '#64748B', fontSize: '0.8125rem' }}>Keep track of all browser releases in one place.</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{ minWidth: 40, width: 40, height: 40, borderRadius: '50%', backgroundColor: isDark ? 'rgba(16,185,129,0.2)' : '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <LinkIcon sx={{ color: '#10B981', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? '#fff' : '#1E293B', mb: 0.5 }}>Share Release Links</Typography>
                  <Typography variant="body2" sx={{ color: isDark ? '#94A3B8' : '#64748B', fontSize: '0.8125rem' }}>Provide secure download or update links.</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{ minWidth: 40, width: 40, height: 40, borderRadius: '50%', backgroundColor: isDark ? 'rgba(168,85,247,0.2)' : '#F3E8FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ArticleIcon sx={{ color: '#A855F7', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? '#fff' : '#1E293B', mb: 0.5 }}>Add Notes</Typography>
                  <Typography variant="body2" sx={{ color: isDark ? '#94A3B8' : '#64748B', fontSize: '0.8125rem' }}>Include important changes or release notes.</Typography>
                </Box>
              </Box>
            </Stack>

            {/* Decorative abstract shape at bottom */}
            <Box sx={{ 
              position: 'absolute', bottom: -50, left: -50, right: -50, height: 250, 
              background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 70%)',
              opacity: isDark ? 0.1 : 0.6,
              zIndex: 0
            }} />
          </Box>

          {/* RIGHT SIDE */}
          <Box sx={{ 
            width: { xs: '100%', md: '65%' }, 
            backgroundColor: isDark ? '#0F172A' : '#fff',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Scrollable Content Area */}
            <Box 
              sx={{ 
                p: { xs: 3, md: 4 }, 
                pt: { xs: 6, md: 4 },
                pb: 2, 
                overflowY: 'auto', 
                flexGrow: 1,
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { background: isDark ? '#475569' : 'var(--hairline, #E2E8F0)', borderRadius: '4px' },
                '&::-webkit-scrollbar-thumb:hover': { background: isDark ? '#64748B' : 'var(--slate, #94A3B8)' }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <IconButton 
                  onClick={() => setModalOpen(false)}
                  sx={{ 
                    border: isDark ? '1px solid #334155' : '1px solid #E2E8F0', 
                    borderRadius: '8px',
                    p: 0.5
                  }}
                >
                  <CloseIcon sx={{ fontSize: 20, color: isDark ? '#94A3B8' : '#64748B' }} />
                </IconButton>
              </Box>

              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isDark ? '#F1F5F9' : '#1E293B', mb: 1 }}>
                    Version <span style={{ color: '#EF4444' }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="e.g. 1.0.23"
                    value={form.version}
                    onChange={(e) => setForm(f => ({ ...f, version: e.target.value }))}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><SellIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: isDark ? '#1E293B' : '#fff',
                        '& fieldset': { borderColor: isDark ? '#334155' : '#E2E8F0' },
                        '&:hover fieldset': { borderColor: isDark ? '#475569' : '#CBD5E1' },
                      }
                    }}
                  />
                  <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B', display: 'block', mt: 0.5 }}>
                    Enter the browser version number (e.g. 1.0.23).
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isDark ? '#F1F5F9' : '#1E293B', mb: 1 }}>
                    Download / Release Link <span style={{ color: '#EF4444' }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="https://example.com/download"
                    value={form.link}
                    onChange={(e) => setForm(f => ({ ...f, link: e.target.value }))}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><LinkIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: isDark ? '#1E293B' : '#fff',
                        '& fieldset': { borderColor: isDark ? '#334155' : '#E2E8F0' },
                        '&:hover fieldset': { borderColor: isDark ? '#475569' : '#CBD5E1' },
                      }
                    }}
                  />
                  <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B', display: 'block', mt: 0.5 }}>
                    Enter the direct download or release link for this version.
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isDark ? '#F1F5F9' : '#1E293B', mb: 1 }}>
                    Notes (optional)
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Add release notes, changes or any important information..."
                    value={form.notes}
                    onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                          <ArticleIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: isDark ? '#1E293B' : '#fff',
                        '& fieldset': { borderColor: isDark ? '#334155' : '#E2E8F0' },
                        '&:hover fieldset': { borderColor: isDark ? '#475569' : '#CBD5E1' },
                      }
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
                      Provide a brief description about this release (optional).
                    </Typography>
                    <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
                      {form.notes ? form.notes.length : 0}/500
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mt: 1 }}>
                  <Checkbox 
                    checked={!!form.isLatest}
                    onChange={(e) => setForm(f => ({ ...f, isLatest: e.target.checked }))}
                    sx={{ p: 0, color: isDark ? '#475569' : '#CBD5E1', '&.Mui-checked': { color: '#0066FF' } }}
                  />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? '#F1F5F9' : '#1E293B' }}>
                      Mark as latest
                    </Typography>
                    <Typography variant="body2" sx={{ color: isDark ? '#94A3B8' : '#64748B', fontSize: '0.8125rem' }}>
                      This version will be shown as the latest release.
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>

            {/* Bottom Actions Footer */}
            <Box sx={{ p: 3, px: { xs: 3, md: 4 }, display: 'flex', justifyContent: 'flex-end', gap: 2, borderTop: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid var(--hairline)', bgcolor: isDark ? '#0F172A' : 'var(--canvas)', flexShrink: 0 }}>
              <Button 
                onClick={() => setModalOpen(false)}
                variant="outlined"
                sx={{
                  color: isDark ? '#F1F5F9' : 'var(--vault-navy)',
                  borderColor: isDark ? '#334155' : 'var(--hairline)',
                  fontFamily: 'Arial',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  textTransform: 'none',
                  borderRadius: '6px',
                  height: '40px',
                  px: 4,
                  '&:hover': {
                    bgcolor: isDark ? '#1E293B' : 'var(--mist)',
                    borderColor: isDark ? '#475569' : 'var(--slate)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={submit}
                disabled={loading}
                sx={{
                  bgcolor: 'var(--edge-blue)',
                  color: '#FFF',
                  fontFamily: 'Arial',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  borderRadius: '6px',
                  height: '40px',
                  px: 4,
                  textTransform: 'none',
                  boxShadow: 'none',
                  border: '1px solid var(--edge-blue)',
                  '&:hover': {
                    bgcolor: 'var(--edge-blue)',
                    boxShadow: '0 0 0 3px var(--blue-tint)',
                  },
                }}
              >
                {loading ? "Saving..." : (editItem ? "Update" : "Add Update")}
              </Button>
            </Box>
          </Box>
        </Stack>
      </Dialog>

      {/* Single Delete Modal */}
      <CommonDeleteModal
        open={deleteOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        isDeleting={deleting}
        entityName={deletingItem ? deletingItem.version : ''}
        entityType="Update"
      />

      {/* Bulk Delete Confirmation Dialog */}
      <CommonDeleteModal
        open={bulkDeleteOpen}
        onClose={handleCancelBulkDelete}
        onConfirm={handleConfirmBulkDelete}
        isDeleting={bulkDeleting}
        entityType="Update"
        isBulk={true}
        bulkCount={selectedIds.length}
      />
    </Box>
  );
}